import React from 'react';
import { Button } from 'react-bootstrap';
import { FileText, Save } from 'lucide-react';
import InfoHint from '../../SharedComponents/InfoHint';
import { toast } from 'sonner';
import { useQuoteSettings } from '../../../../context/QuoteSettingsContext';

// Quote settings section — numbering and validity defaults. Self-contained on
// QuoteSettingsContext. Rendered only when the tenant has `invoicing_quotes`
// (gated in the CompanySettings section registry). Uses the global settings
// styles from the CompanySettings shell.
const QuotesSection = () => {
  const { quoteSettings, updateQuoteSetting, saveQuoteSettings } = useQuoteSettings();

  const saveQuoteNumbering = async () => {
    try {
      await saveQuoteSettings('quoteNumbering');
      toast.success("Quote numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save quote numbering settings");
    }
  };

  return (
    <>
      {/* Quote Numbering Section */}
      <div className="modern-card" id="offertnumrering">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <FileText size={16} />
              Offertnumrering
            </h2>
            <p className="section-subtitle">
              Prefix, startnummer och format för offerter
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={saveQuoteNumbering}
            >
              <Save className="me-2" size={16} />
              Spara offertnumrering
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="quotePrefix" className="modern-label">
                      Prefix
                    </label>
                    <input
                      type="text"
                      className="modern-input"
                      id="quotePrefix"
                      placeholder="Q"
                      value={quoteSettings.quoteNumbering.prefix}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', { prefix: e.target.value })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="quoteStartNumber" className="modern-label">
                      Startnummer
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quoteStartNumber"
                      placeholder="1"
                      value={quoteSettings.quoteNumbering.startNumber}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', {
                        startNumber: parseInt(e.target.value, 10) || 1
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="quotePadding" className="modern-label">
                      Antal siffror
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quotePadding"
                      min="1"
                      max="10"
                      value={quoteSettings.quoteNumbering.padding}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', {
                        padding: parseInt(e.target.value, 10) || 4
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteIncludeYear"
                        checked={quoteSettings.quoteNumbering.includeYear}
                        onChange={(e) => updateQuoteSetting('quoteNumbering', { includeYear: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteIncludeYear">
                        Inkludera år i offertnummer
                      </label>
                    </div>
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteResetYearly"
                        checked={quoteSettings.quoteNumbering.resetYearly}
                        onChange={(e) => updateQuoteSetting('quoteNumbering', { resetYearly: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteResetYearly">
                        Återställ startnummer varje år
                      </label>
                    </div>
                  </div>
                </div>

                {/* Preview for Quote */}
                <div className="form-field full-width settings-preview">
                  <label className="modern-label">Exempel på offertnummer:</label>
                  <div className="settings-preview__value">
                    {quoteSettings.quoteNumbering.includeYear
                      ? `${quoteSettings.quoteNumbering.prefix}${new Date().getFullYear()}-${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                      : `${quoteSettings.quoteNumbering.prefix}${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote-specific settings can be added here */}
      <div className="modern-card" id="offertinstallningar">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              Offertinställningar
            </h2>
            <p className="section-subtitle">
              Hur länge dina offerter ska vara giltiga
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={() => saveQuoteSettings()}
            >
              <Save className="me-2" size={16} />
              Spara offertinställningar
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="quoteValidityDays" className="modern-label">
                      Giltighetstid (dagar)
                      <InfoHint text="Antal dagar som offerten är giltig från skapandedatum" />
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quoteValidityDays"
                      placeholder="30"
                      value={quoteSettings.defaultTerms?.validityDays || 30}
                      onChange={(e) => updateQuoteSetting('defaultTerms', {
                        validityDays: parseInt(e.target.value, 10) || 30
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteAutoConvert"
                        checked={quoteSettings.defaultTerms?.autoConvertToInvoice || false}
                        onChange={(e) => updateQuoteSetting('defaultTerms', {
                          autoConvertToInvoice: e.target.checked
                        })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteAutoConvert" style={{ marginBottom: 0 }}>
                        Automatisk konvertering till faktura när offert accepteras
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuotesSection;
